// Catálogos y ajustes: lectura con sus ids y cuántos registros usan cada cosa, y los cambios uno a uno.
// Lo que ya tiene registros no se borra (la base lo impide): se desactiva o se renombra, como en el prototipo.
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Moneda } from '../logica/dinero';
import type { Cuenta, Plantilla, Transportadora } from '../logica/pedidos';

export type Ambito = 'producto' | 'material' | 'gasto';
export interface Canal { id: number; nombre: string; activo: boolean }
export interface Familia { id: number; ambito: Ambito; nombre: string }
export interface Categoria { id: number; ambito: Ambito; nombre: string; familia_id: number | null; logistica: boolean }
export interface Color { id: number; nombre: string; hex: string; activo: boolean }
export interface CuentaFila extends Cuenta { id: number }
export interface Ajustes {
  canales: Canal[]; familias: Familia[]; categorias: Categoria[]; colores: Color[]; cuentas: CuentaFila[];
  tasas: Record<Moneda, number>;
  envio: { valor: number; activo: boolean }; dias_entrega: number; pasarela: { nombre: string; activa: boolean };
  vigencia: number; anticipo: number; condiciones: string;
  whatsapp_negocio: string;   // dígitos con indicativo; el mismo para todas las campañas
  transportadoras: Transportadora[]; motivos: string[];
  plantillas: Record<'pedido' | 'cotizacion' | 'envio', Plantilla>;
  usoCanal: Map<number, number>; usoCategoria: Map<number, number>; coloresEnProductos: Map<string, number>;
}

async function todas<T>(consulta: (desde: number, hasta: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const out: T[] = [];
  for (let desde = 0; ; desde += 1000) {
    const { data, error } = await consulta(desde, desde + 999);
    if (error) throw new Error(error.message);
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}
const contar = <T,>(filas: T[], k: (f: T) => number | null) => { const m = new Map<number, number>(); for (const f of filas) { const v = k(f); if (v != null) m.set(v, (m.get(v) || 0) + 1); } return m; };

export async function cargarAjustes(sb: SupabaseClient): Promise<Ajustes> {
  const [par, mon, can, fam, cat, col, cue, ped, prod, mat, gas] = await Promise.all([
    sb.from('parametros').select('clave, valor'),
    sb.from('monedas').select('codigo, tasa_cop'),
    sb.from('canales').select('id, nombre, activo').order('id'),
    sb.from('familias').select('id, ambito, nombre').order('nombre'),
    sb.from('categorias').select('id, ambito, nombre, familia_id, logistica').order('nombre'),
    sb.from('colores_tela').select('id, nombre, hex, activo').order('id'),
    sb.from('cuentas_bancarias').select('id, banco, moneda, tipo, numero, titular, documento, activo').order('id'),
    todas<{ canal_id: number }>((a, b) => sb.from('pedidos').select('canal_id').order('id').range(a, b)),
    sb.from('productos').select('categoria_id, colores'),
    sb.from('materiales').select('categoria_id'),
    todas<{ categoria_id: number }>((a, b) => sb.from('gastos').select('categoria_id').order('id').range(a, b)),
  ]);
  const error = [par, mon, can, fam, cat, col, cue, prod, mat].find(r => r.error)?.error;
  if (error) throw new Error(error.message);
  const P = Object.fromEntries((par.data || []).map(r => [r.clave, r.valor]));
  const usoCategoria = contar([...(prod.data || []), ...(mat.data || []), ...gas] as { categoria_id: number | null }[], f => f.categoria_id);
  const coloresEnProductos = new Map<string, number>();
  for (const p of prod.data || []) for (const c of (p.colores as string[] | null) || []) coloresEnProductos.set(c, (coloresEnProductos.get(c) || 0) + 1);
  return {
    canales: (can.data || []) as Canal[], familias: (fam.data || []) as Familia[], categorias: (cat.data || []) as Categoria[],
    colores: ((col.data || []) as Color[]).map(c => ({ ...c, hex: c.hex.trim() })),
    cuentas: ((cue.data || []) as CuentaFila[]).map(c => ({ ...c, moneda: String(c.moneda).trim() as Moneda })),
    tasas: Object.fromEntries((mon.data || []).map(m => [String(m.codigo).trim(), +m.tasa_cop])) as Record<Moneda, number>,
    whatsapp_negocio: P.whatsapp_negocio?.numero || '',
    envio: { valor: +(P.envio_estandar?.valor ?? 0), activo: P.envio_estandar?.activo !== false },
    dias_entrega: +(P.dias_entrega?.dias_habiles ?? 15),
    pasarela: { nombre: P.pasarela?.nombre || 'Mercado Pago', activa: !!P.pasarela?.activa },
    vigencia: +(P.vigencia_cotizacion?.dias ?? 15), anticipo: +(P.anticipo?.pct ?? 50), condiciones: P.condiciones?.texto || '',
    transportadoras: (P.transportadoras || []).map((t: string | Transportadora) => (typeof t === 'string' ? { nombre: t, rastreo: '' } : t)).filter((t: Transportadora) => t.nombre !== 'Otra'),
    motivos: P.motivos_anulacion || [],
    plantillas: { pedido: P.mensaje_pedido || {}, cotizacion: P.mensaje_cotizacion || {}, envio: P.mensaje_envio || {} },
    usoCanal: contar(ped, f => f.canal_id), usoCategoria, coloresEnProductos,
  };
}

// Mensajes de la base en palabras de la usuaria.
function claro(m: string): string {
  if (/duplicate key|unique/i.test(m)) return 'Ya existe uno con ese nombre.';
  if (/foreign key|violates.*constraint.*references|still referenced/i.test(m)) return 'Tiene registros asociados: no se puede borrar. Desactívalo o cámbiale el nombre.';
  if (/check constraint/i.test(m)) return 'La base no acepta ese valor. Revisa el tipo y la moneda de la cuenta.';
  return m;
}
const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(claro(error.message)); };

export async function guardarParametro(sb: SupabaseClient, clave: string, valor: unknown) {
  ok(await sb.from('parametros').upsert({ clave, valor }));
}
export async function guardarTasa(sb: SupabaseClient, codigo: Moneda, tasa: number) {
  ok(await sb.from('monedas').update({ tasa_cop: tasa }).eq('codigo', codigo));
}
type Tabla = 'canales' | 'familias' | 'categorias' | 'colores_tela' | 'cuentas_bancarias';
export async function crear<T>(sb: SupabaseClient, tabla: Tabla, fila: Record<string, unknown>): Promise<T> {
  const { data, error } = await sb.from(tabla).insert(fila).select().single();
  if (error) throw new Error(claro(error.message));
  return data as T;
}
export async function cambiar(sb: SupabaseClient, tabla: Tabla, id: number, cambios: Record<string, unknown>) {
  ok(await sb.from(tabla).update(cambios).eq('id', id));
}
export async function borrar(sb: SupabaseClient, tabla: Tabla, id: number) {
  ok(await sb.from(tabla).delete().eq('id', id));
}

// Un color renombrado sigue en las listas de colores de los productos. Los pedidos guardan el nombre que tenían.
export async function renombrarColor(sb: SupabaseClient, id: number, antes: string, nuevo: string) {
  await cambiar(sb, 'colores_tela', id, { nombre: nuevo });
  const { data, error } = await sb.from('productos').select('sku, colores').not('colores', 'is', null);
  if (error) throw new Error(error.message);
  for (const p of (data || []).filter(x => (x.colores as string[]).includes(antes))) ok(await sb.from('productos').update({ colores: (p.colores as string[]).map(c => (c === antes ? nuevo : c)) }).eq('sku', p.sku));
}
// ¿Algún pedido usa este color? (Se pregunta al borrar, para no leer todas las prendas al abrir Ajustes.)
export async function colorEnPedidos(sb: SupabaseClient, nombre: string): Promise<boolean> {
  const { count, error } = await sb.from('pedido_lineas').select('id', { count: 'exact', head: true }).eq('color_tela', nombre);
  if (error) throw new Error(error.message);
  return (count || 0) > 0;
}
